// The visual parts of an SOP (branch sop-visual; REPORT-sop-visual.md): inline-SVG icons, the PPE icon row and picker, the red / orange /
// blue blocks, a photo or video of a step shown through its signed link (or from the offline pack), the photo markup tool
// (arrow / circle / number), and the "Add photo" part of a step. Pure rules: src/lib/sopVisual.js. Phone first: every button is 44 px or more.
import { useEffect, useRef, useState } from 'react'
import { useSignedUrl, fetchFile } from '../lib/fileAccess'
import { offlineMediaUrl } from '../lib/manualOffline'
import { supabase } from '../lib/supabase'
import { iconSvg, PPE_KEYS, PPE_ICONS, ppeLabel, BRAND, BLOCKS, blockOf, canAdd, shapeFromDrag, addShape, undoShape, drawShapes, mediaCounts, MAX_PHOTOS, MAX_VIDEO_SECONDS } from '../lib/sopVisual'
import { compressImage, loadBitmap, uploadSopPhoto, uploadSopVideo } from '../lib/sopMedia'
import { marksToPixels } from '../lib/sopRecord'

export const touch = { minHeight: 44, minWidth: 44, padding: '10px 14px', fontSize: 15, borderRadius: 10, fontFamily: 'inherit', cursor: 'pointer' }
export const btn = { ...touch, border: '1px solid ' + BRAND.line, background: '#fff', color: BRAND.navy, fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 8, justifyContent: 'center' }
export const btnPrimary = { ...btn, background: BRAND.blue, borderColor: BRAND.blue, color: '#fff' }

export function Ico({ name, size = 22, color, style, label }) {
  return <span aria-hidden={label ? undefined : true} role={label ? 'img' : undefined} aria-label={label} style={{ display: 'inline-flex', lineHeight: 0, flexShrink: 0, ...style }} dangerouslySetInnerHTML={{ __html: iconSvg(name, { size, color: color || 'currentColor', label }) }} />
}

// the signed link of a stored path, or - with no connection - the copy in the offline pack
export function useMediaSrc(path) {
  const signed = useSignedUrl(path)
  const [off, setOff] = useState(null)
  useEffect(() => {
    let live = true
    if (path && !signed) offlineMediaUrl(path).then(u => { if (live && u) setOff(u) })
    return () => { live = false }
  }, [path, signed])
  return signed || off
}
export function Media({ m, alt = '', style, onClick, controls = true }) {
  const src = useMediaSrc(m && m.path)
  const box = { background: BRAND.soft, borderRadius: 12, ...style }
  if (!src) return <div aria-busy="true" style={{ ...box, minHeight: 120, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#4B5B70', fontSize: 13 }}>Photo loading…</div>
  if (m.kind === 'video') return <video src={src} controls={controls} playsInline preload="metadata" aria-label={alt || 'Step video'} style={{ ...box, width: '100%', maxHeight: '60vh', background: '#000' }} />
  return <img src={src} alt={alt || 'Step photo'} onClick={onClick} style={{ ...box, width: '100%', height: 'auto', display: 'block', cursor: onClick ? 'zoom-in' : 'default', ...(style || {}) }} />
}

// ---- PPE: a fixed list, shown as an icon row ----
export function PpeRow({ keys, size = 30, light = false }) {
  const list = (keys || []).filter(k => PPE_ICONS[k])
  if (!list.length) return null
  return (
    <ul aria-label="PPE required" style={{ display: 'flex', flexWrap: 'wrap', gap: 10, listStyle: 'none', margin: 0, padding: 0 }}>
      {list.map(k => (
        <li key={k} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: 76, gap: 4, textAlign: 'center' }}>
          <span style={{ width: size + 22, height: size + 22, borderRadius: '50%', background: light ? '#fff' : BRAND.navy, color: light ? BRAND.navy : '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', border: light ? '2px solid ' + BRAND.navy : 'none' }}><Ico name={k} size={size} /></span>
          <span style={{ fontSize: 12, lineHeight: 1.2, color: light ? '#fff' : BRAND.ink }}>{ppeLabel(k)}</span>
        </li>
      ))}
    </ul>
  )
}
export function PpePicker({ value, onChange }) {
  const on = new Set(value || [])
  return (
    <div role="group" aria-label="PPE" style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
      {PPE_KEYS.map(k => (
        <button key={k} type="button" aria-pressed={on.has(k)} onClick={() => onChange(on.has(k) ? value.filter(x => x !== k) : [...(value || []), k])}
          style={{ ...btn, flexDirection: 'column', gap: 4, width: 92, padding: '8px 4px', fontSize: 12, background: on.has(k) ? BRAND.navy : '#fff', color: on.has(k) ? '#fff' : BRAND.navy, borderColor: on.has(k) ? BRAND.navy : BRAND.line }}>
          <Ico name={k} size={28} /><span>{ppeLabel(k)}</span>
        </button>
      ))}
    </div>
  )
}

// ---- blocks: warning red, caution orange, note blue ----
export function Block({ type, text, big = false, children }) {
  const b = blockOf(type) || BLOCKS.caution
  return (
    <div role="note" style={{ borderLeft: '6px solid ' + b.bar, background: b.bg, color: b.ink, borderRadius: 10, padding: big ? '16px 18px' : '10px 12px', display: 'flex', gap: 12, alignItems: 'flex-start', fontSize: big ? 20 : 14, lineHeight: 1.4 }}>
      <Ico name={b.icon} size={big ? 34 : 22} color={b.bar} />
      <div style={{ flex: 1 }}><div style={{ fontWeight: 800, letterSpacing: '.06em', fontSize: big ? 15 : 11.5 }}>{b.label}</div><div style={{ fontWeight: big ? 600 : 500 }}>{text}</div>{children}</div>
    </div>
  )
}
export const FlagChip = ({ text }) => <span role="status" style={{ display: 'inline-flex', gap: 6, alignItems: 'center', background: '#FDE8E8', color: '#7A1212', border: '1px solid #B3261E', borderRadius: 20, padding: '4px 10px', fontSize: 12.5, fontWeight: 700 }}><Ico name="warning" size={15} color="#B3261E" />{text}</span>

// ---- the markup tool ----
// src = a stored path (an existing photo) or a Blob (a new one); initial = suggested shapes in the photo's pixels; onSave(blob) gets the flat JPEG
export function PhotoMarkup({ src, initial = [], fractions = null, onSave, onCancel, busy = false }) {
  const canvas = useRef(null), bmp = useRef(null), drag = useRef(null)
  const [tool, setTool] = useState('arrow')
  const [shapes, setShapes] = useState(initial)
  const [dims, setDims] = useState({ w: 0, h: 0 })
  const [err, setErr] = useState('')
  useEffect(() => {
    let live = true
    ;(async () => {
      try {
        const blob = src instanceof Blob ? src : await (await fetchFile(src)).blob()
        const b = await loadBitmap(blob)
        if (!live) return
        bmp.current = b; setDims({ w: b.width, h: b.height })
        if (fractions && !initial.length) setShapes(marksToPixels(fractions, b.width, b.height))   // the AI's suggestion: the officer keeps, changes or undoes it
      } catch (e) { if (live) setErr(e.message || 'The photo could not be opened') }
    })()
    return () => { live = false }
  }, [src])
  const paint = (extra) => {
    const c = canvas.current, b = bmp.current
    if (!c || !b) return
    c.width = b.width; c.height = b.height
    const ctx = c.getContext('2d'); ctx.drawImage(b, 0, 0)
    drawShapes(ctx, extra ? [...shapes, extra] : shapes, b.width)
  }
  useEffect(() => { paint() }, [dims, shapes]) // eslint-disable-line
  const at = e => { const r = canvas.current.getBoundingClientRect(); return { x: ((e.clientX - r.left) / r.width) * dims.w, y: ((e.clientY - r.top) / r.height) * dims.h } }
  const down = e => { e.preventDefault(); canvas.current.setPointerCapture(e.pointerId); const p = at(e); drag.current = p; if (tool === 'num') { setShapes(s => addShape(s, { t: 'num', x: p.x, y: p.y })); drag.current = null } }
  const move = e => { if (!drag.current) return; const p = at(e), s = shapeFromDrag(tool, drag.current.x, drag.current.y, p.x, p.y); paint(s) }
  const up = e => { if (!drag.current) return; const p = at(e), s = shapeFromDrag(tool, drag.current.x, drag.current.y, p.x, p.y); drag.current = null; if (s) setShapes(a => addShape(a, s)); else paint() }
  const save = () => canvas.current.toBlob(b => b && onSave(b), 'image/jpeg', 0.85)
  const T = ({ k, icon, label }) => <button type="button" aria-pressed={tool === k} onClick={() => setTool(k)} style={{ ...btn, background: tool === k ? BRAND.blue : '#fff', color: tool === k ? '#fff' : BRAND.navy, borderColor: tool === k ? '#fff' : BRAND.navy, borderWidth: 2 }}><Ico name={icon} size={20} />{label}</button>
  return (
    <div role="dialog" aria-modal="true" aria-label="Mark up the photo" style={{ position: 'fixed', inset: 0, zIndex: 2000, background: BRAND.navy, display: 'flex', flexDirection: 'column' }}>
      <div style={{ padding: 10, display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
        <T k="arrow" icon="arrow" label="Arrow" /><T k="circle" icon="circle" label="Circle" /><T k="num" icon="hash" label="Number" />
        <button type="button" style={btn} disabled={!shapes.length} onClick={() => setShapes(undoShape)}><Ico name="undo" size={20} />Undo</button>
      </div>
      <div style={{ flex: 1, minHeight: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 8 }}>
        {err ? <div role="alert" style={{ color: '#fff' }}>{err}</div>
          : <canvas ref={canvas} aria-label="Photo to mark up" onPointerDown={down} onPointerMove={move} onPointerUp={up} onPointerCancel={() => { drag.current = null; paint() }} style={{ maxWidth: '100%', maxHeight: '100%', touchAction: 'none', borderRadius: 8, background: '#000' }} />}
      </div>
      <div style={{ padding: 10, display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
        <button type="button" style={btn} onClick={onCancel} disabled={busy}>Cancel</button>
        <button type="button" style={btnPrimary} onClick={save} disabled={busy || !dims.w}>{busy ? 'Saving…' : shapes.length ? 'Save with marks' : 'Save photo'}</button>
      </div>
    </div>
  )
}

// ---- "Add photo" on a step: camera on a phone, a file on the desktop; up to 3 photos + 1 video of 30 s ----
// media = the step's media[]; onChange(next media[]); a photo goes through compress -> markup -> upload before it is on the step
export function StepMedia({ media, vesselId, manualId, readOnly, onChange, label = 'step', photoOnly = false, maxPhotos = MAX_PHOTOS, initialMarks = null }) {
  const [pending, setPending] = useState(null)    // { blob, replace?: path, initial }
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState('')
  const c = mediaCounts(media), can = { photo: c.photos < maxPhotos, video: !photoOnly && canAdd(media).video }
  const pickPhoto = async e => {
    const f = e.target.files && e.target.files[0]; e.target.value = ''
    if (!f) return
    setErr('')
    try { const { blob } = await compressImage(f); setPending({ blob }) } catch (x) { setErr('The photo could not be read: ' + (x.message || x)) }
  }
  const pickVideo = async e => {
    const f = e.target.files && e.target.files[0]; e.target.value = ''
    if (!f) return
    setBusy(true); setErr('')
    try { const m = await uploadSopVideo(vesselId, manualId, f); onChange([...(media || []), m]) } catch (x) { setErr(x.message || String(x)) }
    setBusy(false)
  }
  const savePhoto = async blob => {
    setBusy(true); setErr('')
    try {
      const m = await uploadSopPhoto(vesselId, manualId, blob)
      const old = pending && pending.replace
      onChange(old ? (media || []).map(x => (x.path === old ? m : x)) : [...(media || []), m])
      if (old) supabase.storage.from('crew-docs').remove([old]).catch(() => {})
      setPending(null)
    } catch (x) { setErr('Could not save the photo: ' + (x.message || x)) }
    setBusy(false)
  }
  const remove = m => { onChange((media || []).filter(x => x.path !== m.path)); supabase.storage.from('crew-docs').remove([m.path]).catch(() => {}) }
  return (
    <div style={{ marginTop: 8 }}>
      {(media || []).length > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: 8 }}>
          {media.map(m => (
            <div key={m.path} style={{ position: 'relative' }}>
              <Media m={m} alt={'Photo of ' + label} style={{ aspectRatio: '4 / 3', objectFit: 'cover' }} controls />
              {!readOnly && <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
                {m.kind === 'image' && <button type="button" style={{ ...btn, flex: 1, padding: '8px 6px', fontSize: 13 }} onClick={() => setPending({ replace: m.path, src: m.path })} aria-label={'Mark up photo of ' + label}><Ico name="arrow" size={18} />Mark up</button>}
                <button type="button" style={{ ...btn, padding: '8px 10px', color: '#7A1212' }} onClick={() => remove(m)} aria-label={'Remove ' + (m.kind === 'video' ? 'video' : 'photo') + ' of ' + label}><Ico name="trash" size={18} /></button>
              </div>}
            </div>
          ))}
        </div>
      )}
      {!readOnly && (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 8 }}>
          {can.photo && <label style={{ ...btnPrimary, cursor: busy ? 'wait' : 'pointer' }}><Ico name="camera" size={20} />Add photo<input type="file" accept="image/*" capture="environment" onChange={pickPhoto} disabled={busy} style={{ position: 'absolute', width: 1, height: 1, opacity: 0, overflow: 'hidden' }} aria-label={'Add photo to ' + label} /></label>}
          {can.video && <label style={{ ...btn, cursor: busy ? 'wait' : 'pointer' }}><Ico name="video" size={20} />Add video ({MAX_VIDEO_SECONDS} s)<input type="file" accept="video/*" capture="environment" onChange={pickVideo} disabled={busy} style={{ position: 'absolute', width: 1, height: 1, opacity: 0, overflow: 'hidden' }} aria-label={'Add video to ' + label} /></label>}
          <span style={{ alignSelf: 'center', fontSize: 12.5, color: '#4B5B70' }}>{c.photos}/{maxPhotos} photo{maxPhotos === 1 ? '' : 's'}{photoOnly ? '' : ' · ' + c.videos + '/1 video'}</span>
        </div>
      )}
      {busy && <div role="status" style={{ fontSize: 13, color: '#4B5B70', marginTop: 6 }}>Uploading…</div>}
      {err && <div role="alert" style={{ fontSize: 13, color: '#7A1212', marginTop: 6 }}>{err}</div>}
      {pending && <PhotoMarkup src={pending.blob || pending.src} initial={pending.initial || []} busy={busy} onCancel={() => setPending(null)} onSave={savePhoto} />}
    </div>
  )
}
